import React, { useEffect, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import {
  useFetchAppQuery,
} from '../../redux/api/appMarket-api-slice';
import styles from './styles.module.scss';
import { AppDetailsHeader } from '../../components/AppDetails/AppDetailHeader';
import { AppBenefits } from '../../components/AppDetails/AppBenefits';
import { AppDetailedDescription } from '../../components/AppDetails/AppDetailedDescription';
import { AppRatingsAndReviews } from '../../components/AppDetails/AppReviewsAndRatings';
import { AppDetailsSideContent } from '../../components/AppDetails/AppSideContent';
import { AppPricingAndSubscriptionOptions } from '../../components/AppDetails/AppPricingAndSubscription/option-list';
import { getSubscription, isHipaaComplaintApp } from '../../component-helpers/app-details';
import {
  AppDetailsActionType, AppDetailsModal,
  AppDetailsModuleId,
  AppDetailsState,
  ReducerAction,
} from './types';
import {
  appHasRatingsAndReviewsPermission,
  hasMinReviewsToDisplayAggregateRating,
} from '../../component-helpers/app-reviews';
import { useRatingsAndReviews } from '../../contexts/providers/ratings-reviews';
import { DisplayHipaaBanner } from '../../components/AppDetails/HipaaBanner';
import { smoothScrollToElement } from '../../utils/dom-helpers';
import { AppMarketRouteComponentProps } from '../../AppRoutes';
import { AppPricingAndSubscriptionModal } from '../../components/AppDetails/AppActions/SelectPricing';
import { SetPageTitle } from '../../components/Common/SetPageTitle';
import { useTranslation } from 'react-i18next';
import { useHandlePostRequestResponse } from '../../hooks/useHandlePostRequestResponse';
import { RequiresAnotherPlan } from '../../components/AppDetails/RequiresAnotherPlan';
import { NotificationVariant } from '../../components/Common/Notifications/types';
import { NotificationContainer } from '../../components/Common/Notifications';
import { YelpConnectModal } from '../../components/AppDetails/AppActions/YelpConnect';
import { BackgroundShimmer } from 'components/Shimmer';

const initialState = {
  app: undefined,
  subscriptionObj: undefined,
  canShowAggregateRating: false,
  hasRatingsAndReviewsEnabled: false,
};

const reducer = (state: AppDetailsState, action: AppDetailsActionType) => {
  switch (action.type) {
    case ReducerAction.Init:
      return {
        ...state,
        ...action.payload,
      };
    case ReducerAction.SetModalVisibility:
      return {
        ...state,
        openModal: action.payload,
      };
    case ReducerAction.SetRequiresAnotherPlanError:
      return {
        ...state,
        showRequireAnotherPlanError: action.payload,
      };
    default:
      return state;
  }
};

export const AppDetailsPage: React.FC<AppMarketRouteComponentProps> = ({ history, routes, appMode }) => {
  const [appDetailsState, dispatch] = useReducer(reducer, initialState);
  const ratingsAndReviewsContext = useRatingsAndReviews();
  const { t, i18n: { language } } = useTranslation();
  const params: { appId: string } = useParams();
  const appId = params.appId;
  const { data: appDetails, isLoading } = useFetchAppQuery(appId);
  const handlePostReq = useHandlePostRequestResponse();
  useEffect(() => {
    if (appDetails) {
      const subscriptionObj = getSubscription(appDetails);
      const hasRatingsAndReviewsEnabled = appHasRatingsAndReviewsPermission(ratingsAndReviewsContext, appDetails?.id, appDetails?.developer.id);
      const reviewCountGreaterThenThreshold = hasMinReviewsToDisplayAggregateRating(ratingsAndReviewsContext, appDetails?.aggregateRating.reviewCount);
      const canShowAggregateRating = hasRatingsAndReviewsEnabled && reviewCountGreaterThenThreshold;
      dispatch(
        {
          type: ReducerAction.Init,
          payload: {
            app: appDetails,
            subscriptionObj,
            canShowAggregateRating,
            hasRatingsAndReviewsEnabled,
          },
        },
      );
    }
  }, [appDetails, ratingsAndReviewsContext, language]);

  const setModalVisibility = (modal?: AppDetailsModal) => {
    dispatch({ type: ReducerAction.SetModalVisibility, payload: modal });
  };

  const setAnotherPlanError = () => {
    if (!appDetailsState.showRequireAnotherPlanError) dispatch({
      type: ReducerAction.SetRequiresAnotherPlanError,
      payload: true,
    });
  };

  const gotoPricingModule = () => {
    smoothScrollToElement(AppDetailsModuleId.Pricing);
  };

  if (isLoading && !appDetailsState.app) {
    return <BackgroundShimmer smallShimmer={false} />;
  } else {
    return (
      <div className='app-details-page'>
        <SetPageTitle title={t('clover_app_store.app_details_page_title', {
          appName: appDetails?.name.capitalize(),
          developerName: appDetails?.developer.name,
        })} />
        <div className={styles.layoutWrapper}>
          {!isLoading && appDetailsState.app && (
            <>
              {!isHipaaComplaintApp(appDetailsState.app) &&
              <DisplayHipaaBanner installed={appDetailsState.app.installed} />}
              {
                appDetailsState.showRequireAnotherPlanError && <NotificationContainer
                  notificationData={{
                    title: 'merchant.plans.plan_does_not_support_app',
                    variant: NotificationVariant.Yellow,
                  }
                  }
                  notificationProperties={{
                    closeTimer: false,
                    closeable: true
                  }}
                />
              }
              <AppDetailsHeader
                gotoPricingModule={gotoPricingModule}
                ratingsAndReviewsId={AppDetailsModuleId.Ratings}
                state={appDetailsState}
                uninstallCallBack={handlePostReq}
                setModalVisibility={setModalVisibility}
              />
              <div className={styles.flex}>
                <div className={styles.mainContent}>
                  {isHipaaComplaintApp(appDetailsState.app) && (
                    <RequiresAnotherPlan
                      app={appDetailsState.app}
                      setAnotherPlanError={setAnotherPlanError}
                    />
                  )}
                  <AppBenefits app={appDetailsState.app} />
                  <AppDetailedDescription app={appDetailsState.app} />
                  <AppPricingAndSubscriptionOptions
                    app={appDetailsState.app}
                    moduleId={AppDetailsModuleId.Pricing}
                    subscriptionObj={appDetailsState.subscriptionObj}
                    setModalVisibility={setModalVisibility}
                  />
                  {
                    appDetailsState.hasRatingsAndReviewsEnabled &&
                    <AppRatingsAndReviews
                      moduleId={AppDetailsModuleId.Ratings}
                      state={appDetailsState}
                      handlePostReq={handlePostReq}
                    />
                  }
                </div>
                <div className={styles.sideContent}>
                  <AppDetailsSideContent app={appDetailsState.app} />
                </div>
              </div>
              {appDetailsState.openModal === AppDetailsModal.Pricing && <AppPricingAndSubscriptionModal
                app={appDetailsState.app}
                appSubscriptionSelectHandler={handlePostReq}
                open={true}
                closePricingModal={() => setModalVisibility()}
                subscriptionObj={appDetailsState.subscriptionObj}
              />}
              {appDetailsState.openModal === AppDetailsModal.YelpConnect && <YelpConnectModal
                app={appDetailsState.app}
                open={true}
                closeYelpModal={() => setModalVisibility()}
              />}
            </>
          )}
        </div>
      </div>
    );
  }
};
